import { css } from 'aphrodite';
import * as React from 'react';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { getIndustries, getIndustryName } from '../../../models/UiUtils';
import { Industry, KnownCategories } from '../../../viewmodels/AppViewModels';
import FinraImage from '../../assets/finra.png';
import { Button } from '../../components-v2/Button';
import { InputGroupAddon, InputGroupInput, InputGroupV2 } from '../../components-v2/InputGroup';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components-v2/Select';
import { NewCampaignButtons } from '../../components/NewCampaignButtons';
import { SearchIcon2 } from '../../components/svgs/icons/SearchIcon2';
import { TabList, TabRoot, TabTrigger } from '../../components-v2/Tabs';
import { bs } from '../../styles/styles';
import { styleSheet } from './styles';

const SEARCH_DELAY = 600;

interface CampaignBrowserProps {
	categories: string[];
	category: string;
	children: React.ReactNode;
	industry?: string;
	userSelector?: React.ReactNode;
	onCategoryChange?: (selectedCategory: string) => void;
	onIndustryChange?: (selectedIndustry: Industry) => void;
	onSearchChange?: (value: string) => void;
	renderRightActions?: React.ReactNode;
	search?: string;
	searchPlaceholder?: string;
	disableSearch?: boolean;
}

export const CampaignBrowser: React.FC<CampaignBrowserProps> = ({
	categories,
	category,
	children,
	industry,
	userSelector,
	onCategoryChange,
	onIndustryChange,
	onSearchChange,
	renderRightActions,
	search,
	searchPlaceholder = 'Search campaigns or keywords',
	disableSearch,
}) => {
	const userSession = useUserSession();
	const [searchInputValue, setSearchInputValue] = React.useState('');
	React.useEffect(() => {
		const timeout = setTimeout(() => {
			onSearchChange?.(searchInputValue);
		}, SEARCH_DELAY);
		return () => clearTimeout(timeout);
	}, [searchInputValue, onSearchChange]);
	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchInputValue(event.target.value);
	};
	const handleClearSearch = () => {
		setSearchInputValue('');
		onSearchChange?.('');
	};
	const industryOptions = getIndustries({
		accountId: userSession.account.id,
		brokerDealer: userSession.account.additionalInfo?.brokerDealer,
	}).map(value => ({
		label: getIndustryName(value),
		value,
	}));

	return (
		<div className={`${css(styleSheet.containerComponent)} campaigns-browser`}>
			<div
				className={css(
					styleSheet.searchSection,
					onSearchChange && onIndustryChange ? styleSheet.rowLayout : styleSheet.rightAlign
				)}
			>
				{onSearchChange && onIndustryChange && (
					<div className={css(styleSheet.industrySection)}>
						<InputGroupV2>
							<InputGroupInput
								styles={[styleSheet.searchBoxInput]}
								id='campaign-browser-search'
								onChange={handleSearchChange}
								placeholder={searchPlaceholder}
								type='text'
								value={searchInputValue}
								disabled={disableSearch}
							/>
							<InputGroupAddon>
								<SearchIcon2 />
							</InputGroupAddon>
						</InputGroupV2>
						{search && (
							<Button variant='secondary' onClick={handleClearSearch}>
								Clear search
							</Button>
						)}

						<div className={css(styleSheet.industryDropdown)}>
							<div className={css(styleSheet.browse)}>Browse Industry:</div>
							<Select items={industryOptions} value={(industry as Industry) ?? null} onValueChange={onIndustryChange}>
								<SelectTrigger styles={[styleSheet.dropdownTrigger]}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{industryOptions.map(industryOption => (
										<SelectItem key={industryOption.label} value={industryOption.value}>
											{industryOption.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						{userSelector}
					</div>
				)}
				{renderRightActions ? renderRightActions : <NewCampaignButtons />}
			</div>
			{categories.length && !search ? (
				<TabRoot value={category} onValueChange={onCategoryChange}>
					<TabList>
						{categories.map(c => {
							if (!c) return null;
							if (c === KnownCategories.FinraReviewed) {
								return (
									<TabTrigger key={c} value={c}>
										<img src={FinraImage} height={10} />
										<span className={css(styleSheet.reviewed)}> Reviewed</span>
									</TabTrigger>
								);
							}
							return (
								<TabTrigger key={c} value={c} styles={[bs.shrink0]}>
									{c}
								</TabTrigger>
							);
						})}
					</TabList>
				</TabRoot>
			) : null}
			{children}
		</div>
	);
};

export * from './hooks';
